<template>
  <Card class="h-full">
    <CustomSuspense :is-loading="isLoading">
      <template #loading>
        <LoadingSpinner class="!h-[500px]"/>
      </template>
      <template #default>
        <div
          v-if="data"
          class="flex flex-col justify-start gap-y-4 p-5"
          ref="chartRef"
        >
          <div class="flex items-center justify-between gap-x-2">
            <strong class="mr-auto text-xl text-primary">{{ data.chartName }}</strong>
            <AnalyticsChartOptions
              v-if="showMenu && chartRef"
              :chart-ref="chartRef"
              :filename="filename"
              :name="name"
              :api="api"
              :chart-download-param="chartDownloadParam"
            />
          </div>
          <div
            class="hide-capture flex items-center justify-end gap-x-2"
            data-html2canvas-ignore
          >
            <AnalyticsSingleDatePicker
              v-if="singleSelect"
              class="w-fit mobile:w-full"
              :date="paramRef.to"
              @change-selected-date="changeSelectedDate"
              min-date="2025-06-14"
            />
            <BcPeriodPicker
              v-else
              :period-list="periodList"
              class="flex !w-fit mobile:hidden"
              :default-selected-period="paramRef.period"
              @get-period="changePeriod"
              :st-date="paramRef.from ?? yesterday.minus({ day: 1 }).toFormat(DATE_FORMATS.dateOnlyDash)"
              :end-date="paramRef.to ?? yesterday.toFormat(DATE_FORMATS.dateOnlyDash)"
              is-dark
              :is-range="!singleSelect"
              :max-date="yesterday.toFormat(DATE_FORMATS.dateOnlyDash)"
            />
            <BcSelect
              v-if="intervalList"
              v-model="paramRef.interval"
              :options="intervalList"
              bold
            />
          </div>
          <BcChartBody
            :high-chart-options="highChartOptions"
            class="h-[400px]"
          />
          <BcPeriodPicker
            v-if="!singleSelect"
            :period-list="periodList"
            class="hidden mobile:flex"
            :default-selected-period="paramRef.period"
            @get-period="changePeriod"
            :st-date="paramRef.from"
            :end-date="paramRef.to"
            is-dark
            :is-range="!singleSelect"
          />
          <div
            v-if="data.chartDescription"
            class="flex flex-col gap-y-2 text-base"
          >
            <strong class="text-primary/40">Description</strong>
            <p class="whitespace-pre-wrap text-primary">
              {{ data.chartDescription }}
            </p>
          </div>
          <span class="py-2 text-base text-primary/40">Powered By Xangle</span>
        </div>
      </template>
    </CustomSuspense>
  </Card>
</template>

<script setup lang="ts">
import Highcharts from 'highcharts'
import {DateTime} from 'luxon'
import {BcChartBody, currencyFormatter} from 'xangle-charts'
import {BcPeriodPicker} from 'xangle-ui'

import AnalyticsChartOptions from '~/components/@pages/analytics/AnalyticsChartOptions.vue'
import AnalyticsSingleDatePicker from '~/components/@pages/analytics/AnalyticsSingleDatePicker.vue'
import CustomSuspense from '~/components/suspense/CustomSuspense.vue'
import {useExplorerChart} from '~/composables/useExplorerChart'
import {useResponsiveScreen} from '~/composables/useResponsiveScreen'
import {ANALYTICS_INTERVAL_LABEL, ANALYTICS_PERIOD_LIST} from '~/constants/analytics.constants'
import {IAnalyticsChartInfoOption, IAnalyticsInterval, IAnalyticsParam, IAnalyticsPeriod} from '~/types/analytics.type'
import BcSelect from '~/ui/Select/BcSelect.vue'
import {DATE_FORMATS, millisecondsToDate} from '~/utils/dateFormat'

interface IProps extends IAnalyticsChartInfoOption {
  showMenu?: boolean
  name: string
}

const props = withDefaults(defineProps<IProps>(), {
  periodList: () => ANALYTICS_PERIOD_LIST,
  intervalList: undefined,
  showMenu: true,
  singleSelect: false,
  unit: '',
  allowDecimals: true
})

const yesterday = DateTime.now().toUTC().minus({day: 1})

const paramRef = ref<IAnalyticsParam>({
  period: props.singleSelect ? IAnalyticsPeriod.SELECTOR : IAnalyticsPeriod.M6,
  interval: props.intervalList ? IAnalyticsInterval.DAY : undefined,
  from: undefined,
  to: props.singleSelect ? yesterday.toFormat(DATE_FORMATS.dateOnlyDash) : undefined,
})

const chartDownloadParam = computed<IAnalyticsParam>(() => {
  return {
    period: IAnalyticsPeriod.ALL,
    interval: paramRef.value.interval,
    to: paramRef.value.to,
  }
})

const chartRef = ref<HTMLElement | null>(null)

const {data, isLoading} = props.api(paramRef)

const {isMobile} = useResponsiveScreen()

/** x축 tick을 데이터 기준으로 설정할지 판별 */
const showXAxisByData = computed(() => {
  if (!props.intervalList || isMobile.value) {
    return false
  }
  /** interval === weekly or monthly 일 경우 */
  return paramRef.value.interval !== IAnalyticsInterval.DAY
})

function defaultXAxisTickPositioner(this: Highcharts.Axis): Highcharts.AxisTickPositionerCallbackFunction {
  if (showXAxisByData.value) {
    return this.series[0].xData
  }
}

function defaultXAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
  if (paramRef.value.interval == IAnalyticsInterval.MONTH) {
    return millisecondsToDate(Number(this.value), 'MMM yyyy', 'utc')
  }
  return millisecondsToDate(Number(this.value), 'MMM dd', 'utc')
}

function defaultTooltipFormatter(this: Highcharts.TooltipFormatterContextObject) {
  let date
  switch (paramRef.value.interval) {
    case IAnalyticsInterval.WEEK: {
      const isLastPoint = this.point.index === this.series.data.length - 1
      /** 마지막 포인트라면 어제까지의 날짜 표시, 아니라면 다음 포인트 이전 날짜까지 표시 */
      const lastDate = isLastPoint ? yesterday.toMillis() : (parseInt(this.series.xData[this.point.index + 1]) - 86400000)
      date = `${millisecondsToDate(Number(this.x), 'MMM dd, yyyy', 'utc')} ~ ${millisecondsToDate(lastDate, 'MMM dd, yyyy', 'utc')}`
      break
    }
    case IAnalyticsInterval.MONTH: {
      date = millisecondsToDate(Number(this.x), 'MMM yyyy', 'utc')
      break
    }
    default: {
      date = millisecondsToDate(Number(this.x), 'MMM dd, yyyy', 'utc')
      break
    }
  }
  const pointsArray = this.points ?? [this.point]
  const pointsTooltip = pointsArray.map((point) => {
    return `<div class="flex items-center gap-x-1">
              <div
                class="h-2 w-2 rounded-full"
                style="background-color: ${point.color}">
              </div>
              <span>${point.series.name}: ${currencyFormatter({
      number: point.y,
      unit: props.unit,
      decimalPoint: 2,
      contraction: true,
      showZero: true,
      showDecimalZero: false,
    })}</span>
            </div>`
  }).join('')
  return `<div class="flex flex-col gap-y-2 rounded-lg border border-primary/20 bg-primary/10 p-2 text-primary text-base backdrop-blur-lg">
            <strong class="font-bold">${date} (UTC)</strong>
            ${pointsTooltip}
          </div>`
}

const options = computed<Highcharts.Options>(() => {
  if (!data.value) {
    return {}
  }
  return {
    legend: {
      enabled: data.value.chartDataList.length > 1,
    },
    xAxis: {
      labels: {
        formatter: defaultXAxisLabelFormatter,
      },
      tickPositioner: defaultXAxisTickPositioner,
    },
    yAxis: Array(2).fill(0).map(() => {
      return {
        allowDecimals: props.allowDecimals,
        tickAmount: props.yTickAmount,
      }
    }),
    tooltip: {
      formatter: defaultTooltipFormatter,
    }
  }
})

const {chartOptions, highChartOptions} = useExplorerChart(options)

const changeSelectedDate = (date: string) => {
  paramRef.value.to = date
}

const changePeriod = (item: { selectedTab: string, start: string, end: string }) => {
  if (item.selectedTab === IAnalyticsPeriod.SELECTOR) {
    paramRef.value.from = item.start
    paramRef.value.to = item.end
  } else {
    paramRef.value.from = undefined
    paramRef.value.to = undefined
  }
  paramRef.value.period = item.selectedTab as IAnalyticsPeriod
}

const filename = computed(() => {
  if (!data.value) {
    return 'download'
  }
  if (paramRef.value.interval) {
    return `${data.value.getFileName()}_${ANALYTICS_INTERVAL_LABEL[paramRef.value.interval]}`
  }
  return data.value.getFileName()
})

watch(() => data.value, () => {
  if (data.value) {
    if (data.value.chartDataList.length > 1) {
      chartOptions.yAxisOptions.left.title = data.value.chartDataList[0].name
      chartOptions.yAxisOptions.right.title = data.value.chartDataList[1].name
    }
    chartOptions.chartDataList = data.value.chartDataList
    chartOptions.yAxisOptions.left.max = props.yMax ?? null
    chartOptions.yAxisOptions.left.unit = props.unit
  }
}, {deep: true, immediate: true})
</script>

<style scoped lang="scss">
:deep(.highcharts-range-selector-group) {
  @apply hidden;
}

:deep(.highcharts-legend-item-hidden) {
  * {
    @apply font-default fill-primary/20 text-primary/20 #{!important};
  }
}

:deep(.bc-tab-container) {
  @apply bg-primary/5 #{!important};

  .bc-tab {
    @apply text-primary/40 #{!important};

    &:has(svg) * {
      @apply opacity-100 fill-primary/40 #{!important};
    }

    &:hover {
      @apply bg-primary/10 text-primary #{!important};

      &:has(svg) * {
        @apply fill-primary #{!important};
      }
    }

    &.active, &.focus {
      &:has(svg) * {
        @apply fill-primary #{!important};
      }

      @apply bg-explorer-blue text-primary #{!important};
    }
  }
}
</style>